/**
 * Custom hook to get environment variables
 * @returns {{
 *   getEnv: (function(string): (string|undefined)),
 *   getEnvJson: (function(string): (any|null)),
 *   getEnvInt: (function(string, number): number),
 *   isProduction: boolean,
 *   isEnvTruthy: (function(string): boolean),
 *   isDev: boolean
 * }}
 */
const useEnv = () => {
    const isProduction = import.meta.env.PROD;
    const isDev = import.meta.env.DEV;

    /**
     * Get env variable from import.meta.env or URL query params (in dev)
     * @param {string} key
     * @returns {string|undefined}
     */
    const getEnv = (key) => {
        const wholeKey = `VITE_${key}`;
        // In development mode, we can get the env variable from the URL (for testing purposes)
        if (isDev) {
            const params = new URLSearchParams(document.location.search);
            if (params.has(wholeKey)) {
                return params.get(wholeKey);
            }
        }
        return import.meta.env[wholeKey];
    }

    /**
     * Get env variable JSON value parsed as object
     * @param key
     * @returns {any|null}
     */
    const getEnvJson = (key) => {
        const value = getEnv(key);
        if (value !== undefined) {
            try {
                return JSON.parse(value);
            } catch (error) {
                if (isDev) {
                    console.log('Unable to parse env json: ', value, error);
                }
                return null;
            }
        }
        return null;
    }

    /**
     * Get env variable value parsed as a non-negative integer
     * @param {string} key
     * @param {number} fallback - Returned when the variable is missing, not a number or negative
     * @returns {number}
     */
    const getEnvInt = (key, fallback) => {
        const parsed = Number.parseInt(getEnv(key), 10);
        return Number.isNaN(parsed) || parsed < 0 ? fallback : parsed;
    }

    /**
     * Return true if env variable is set to 1
     * @param {string} key
     * @returns {boolean}
     */
    const isEnvTruthy = (key) => {
        return getEnv(key)?.toString() === '1' || getEnv(key)?.toString() === 'true';
    }

    return {
        isProduction,
        isDev,
        getEnv,
        isEnvTruthy,
        getEnvJson,
        getEnvInt
    }
}

export default useEnv;
